// Import react elements
import React, { useState, useEffect } from 'react';

// Import hooks
import { usePersistentFilters } from 'global/hooks/usePersistentFilters';

// Import components
import CustomisableTable from 'global/components/CustomisableTable';
import CreateDateFilterPickers from 'global/components/CreateDateFilterPickers';
import CreateToggleFilters from 'global/components/CreateToggleFilters';
import CustomisableTableSelectedRowsActionsDropwdown from 'global/components/CustomisableTableActionsDropdown';

// Import utilities
import { 
    formatDateFromMidnightString,
    formatDateToMidnightString,
    getDateDaysAgo,
    getMonthToDateStartDate,
    getYearToDateStartDate,
 } from "global/utilities/ConvertDate";
import { LoadingSpinner } from 'global/components/loading_ui/LoadingSpinnerAnimation';





const OrdersTable = ({ initialTableColumns, ordersData, uniqueFilterOptions, defaultFilters, handleSelectOrder, fetchOrders, quickSearchControl, loadingOrders, loadingOrdersErrorMessage, orderActions, showTax, setShowTax }) => {

    //console.log("log: ", ordersData);


    // Set boolean state for displaying quick checkbox filters
    const [toggleTableQuickFilters, setToggleQuickFilters] = useState(false);

    // Set empty array state for selected order/s using check box in table - array of order IDs
    const [selectedOrderRows, setSelectedOrderRows] = useState([]);

    // Set state for number of displayed order/s 
    const [numberOfDisplayedOrders, setNumberOfDisplayedOrders] = useState(0);

    // Your filter keys you want to persist
    const filterKeys = ['status', 'connectionname'];

    // This object maps internal filter keys to user-friendly labels
    const filterLabels = {
        connectionname: 'Connection',
        status: 'Status',
    };


    // Set today's date
    const today = new Date().toISOString().split("T")[0];
   
    // This creates a Date object for today
    const previousMonth = new Date();

    // This moves the date back by one calendar month
    previousMonth.setMonth(previousMonth.getMonth() - 1);

    // This formats the date as YYYY-MM-DD
    const defaultStartDate = previousMonth.toISOString().split("T")[0];

    // Date range filter state
    const [searchFromDate, setSearchFromDate] = useState(defaultStartDate);
    const [searchToDate, setSearchToDate] = useState(today);

    // This stores the date range currently used to fetch orders
    const [activeOrderDateRange, setActiveOrderDateRange] = useState({
        startDate: defaultStartDate,
        endDate: today,
    });

    // Function to handle date filter submit button click
    const handleOrdersDatePickerSubmit = () => {

        // If there's no start or end date then return early
        if (!searchFromDate || !searchToDate) return;

        // Clear selected rows
        setSelectedOrderRows([]);

        const formattedFromDate = formatDateFromMidnightString(searchFromDate);
        const formattedToDate = formatDateToMidnightString(searchToDate);

        // Otherwise fetch the orders by giving the dates to the api
        fetchOrders("", formattedFromDate, formattedToDate);

        // Store the dates that were just searched
        setActiveOrderDateRange({
            startDate: searchFromDate,
            endDate: searchToDate,
        });
         
    };



    // This applies one of the preset all-orders date ranges
    const handleOrdersDatePresetClick = (startDate, endDate = today) => {

        // Clear selected rows
        setSelectedOrderRows([]);

        // Store the preset dates in the date pickers
        setSearchFromDate(startDate);
        setSearchToDate(endDate);

        // Format the preset dates for the API
        const formattedFromDate = formatDateFromMidnightString(startDate);
        const formattedToDate = formatDateToMidnightString(endDate);

        // Fetch the preset date range
        fetchOrders("", formattedFromDate, formattedToDate);

        // Store the dates that were just searched
        setActiveOrderDateRange({
            startDate: startDate,
            endDate: endDate,
        });

    };



    // Extract form-related state and functions from the `usePersistentFilters` custom hook  (for orders table filters)
    const {
        selectedFilters,
        setSelectedFilters,
        toggleFilterValue,
        setFilterGroupAllOn,
        setFilterGroupAllOff,
        resetFilterGroup,
    } = usePersistentFilters("ordersTable", filterKeys, defaultFilters);


    // This resets the selected filters whenever the default filters change
    useEffect(() => {

        // This updates the selected filters to match the latest defaults
        setSelectedFilters(defaultFilters);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultFilters]);


    // Render the date range and useful recent-order presets
    const renderOrderDateTools = () => {

        return (

            <div className="orders-date-tools">

                <CreateDateFilterPickers
                    filterStartDate={searchFromDate}
                    setFilterStartDate={setSearchFromDate}
                    filterEndDate={searchToDate}
                    setFilterEndDate={setSearchToDate}
                    maxDate={today}
                    handleDateFilterSubmit={handleOrdersDatePickerSubmit}
                    buttonTitle="Search"
                    activeStartDate={activeOrderDateRange.startDate}
                    activeEndDate={activeOrderDateRange.endDate}
                    datePickerMessage={null}
                />

                <div className="orders-date-presets">

                    <button type="button" className="orders-date-presets__button" onClick={() => handleOrdersDatePresetClick(getDateDaysAgo(30))}>
                        30 days
                    </button>

                    <button type="button" className="orders-date-presets__button" onClick={() => handleOrdersDatePresetClick(getDateDaysAgo(60))}>
                        60 days
                    </button>

                    <button type="button" className="orders-date-presets__button" onClick={() => handleOrdersDatePresetClick(getDateDaysAgo(90))}>
                        90 days
                    </button>

                    <button type="button" className="orders-date-presets__button" onClick={() => handleOrdersDatePresetClick(getMonthToDateStartDate())}>
                        Month to date
                    </button>

                    <button type="button" className="orders-date-presets__button" onClick={() => handleOrdersDatePresetClick(getYearToDateStartDate())}>
                        Year to date
                    </button>

                </div>

            </div>

        );

    };



    // Not needed yet, MVP
    /*
    const handleToggleShowTax = () => {

        // Switch the value in state (this won't update the table because we need to fetch and reformat it)
        setShowTax((prev) => !prev);

    }
    */

    

    // Check if any checkbox filters have been selected - so we can change the collapse/expand icon colour
    const anyFiltersSelected = Object.values(selectedFilters).some((selectedFilter) => selectedFilter.length > 0);



    // This compares two filter value arrays without caring about order
    const filterValuesMatch = (firstValues, secondValues) => {

        // Normalise the first values
        const safeFirstValues = Array.isArray(firstValues) ? firstValues.map((value) => String(value)).sort() : [];

        // Normalise the second values
        const safeSecondValues = Array.isArray(secondValues) ? secondValues.map((value) => String(value)).sort() : [];

        // Compare the normalised values
        return JSON.stringify(safeFirstValues) === JSON.stringify(safeSecondValues);

    };



    // This normalises filter values for comparison
    const normaliseFilterValues = (filterValues) => {

        // Return an empty array for missing values
        if (!Array.isArray(filterValues)) {

            return [];

        }

        // Return a string array, allowing for either plain values or option objects
        return filterValues
            .map((filterValue) => {

                // Handle option objects
                if (filterValue && typeof filterValue === "object") {

                    return String(filterValue.value ?? filterValue.key ?? filterValue.id ?? filterValue.name ?? filterValue.label ?? "");

                }

                // Handle plain values
                return String(filterValue ?? "");

            })
            .filter((filterValue) => filterValue !== "");

    };



    // This checks whether the current quick filters are restricting the available table options
    const tableFiltersAreApplied = filterKeys.some((filterKey) => {

        // Read available values for this filter group
        const availableValues = normaliseFilterValues(uniqueFilterOptions?.[filterKey]);

        // Read selected values for this filter group
        const selectedValues = normaliseFilterValues(selectedFilters?.[filterKey]);

        // Do not mark empty groups as filtered
        if (availableValues.length === 0) {

            return false;

        }

        // Return whether selected values differ from all available values
        return !filterValuesMatch(selectedValues, availableValues);

    });



    // This stores the total order count before table filters are applied
    const totalOrderCount = Array.isArray(ordersData) ? ordersData.length : 0;



    return (

        <div className="date_picker_and_table">

            <div className="filters">

                {renderOrderDateTools()}

                {/* Keep direct order lookup on the same level as the date filters. */}
                {quickSearchControl}

            </div>

            <div className="orders-table-status-row">

                <div className="orders-table-status-left">

                    {loadingOrders &&

                        <span className="orders-table-status-text">
                            Loading orders...
                        </span>

                    }

                    {!loadingOrders && tableFiltersAreApplied &&

                        <span className="orders-table-status-text">

                        { /* Total number of orders / total selected orders out of total orders PLUS options drop down - Taken out MVP
                        <div className="orders_tally" style={{ opacity: selectedOrderRows.length > 0 ? "1" : "0.5" }}>

                            { /* Clear selected rows 
                            {selectedOrderRows.length > 0 &&
                                <div className="clear_selected_tally" onClick={()=>setSelectedOrderRows([])}>
                                    ⨯
                                </div>
                            }
                            <div>

                                <span>

                                    {loadingOrders ? 

                                        "Loading..."
                                    
                                        : 
                                    
                                        <>
                                            
                                            {/* Display number of selected orders 
                                            {selectedOrderRows.length > 0 ? selectedOrderRows.length + " of " :  ""}

                                            {/* Display total number of order/s  
                                            {numberOfDisplayedOrders} Order{numberOfDisplayedOrders === 1 ? "" : "s" }
                                            
                                        </>

                                    }

                                    </span>
                            </div>

                        </div> 
                        */}
                        
                        Filters applied. Showing {numberOfDisplayedOrders} of {totalOrderCount} order{totalOrderCount === 1 ? "" : "s"}.

                        </span>

                    }

                </div>

                <div className="orders-table-status-right">

                    {selectedOrderRows.length > 0 &&

                        <button type="button" className="orders-table-status-clear" onClick={() => setSelectedOrderRows([])}>
                            Clear selection
                        </button>

                    }

                    {selectedOrderRows.length > 0 &&

                        // Show order actions dropdown 
                        <CustomisableTableSelectedRowsActionsDropwdown tableType={"Orders"} tableData={ordersData} fetchTableData={fetchOrders} handleSelectRow={handleSelectOrder} selectedTableRows={selectedOrderRows} setSelectedRows={setSelectedOrderRows}  />
                                                
                    }

                </div>

            </div>

            {loadingOrders ? 
            
                <LoadingSpinner />

            :

            <div style={{ overflowX: 'auto', minWidth: '100%' }}>

                <div className={"quick_filters_and_table " + ((Object.keys(uniqueFilterOptions).length > 0) ? "display-filters" : "hidden-filters")}>

                    {(Object.keys(uniqueFilterOptions).length > 0) &&

                    <div className={"collapsable_quick_filters " + (toggleTableQuickFilters ? "table__quick_filters--open" : "table__quick_filters--closed")}>

                        {!toggleTableQuickFilters && (Object.keys(uniqueFilterOptions).length > 0) &&

                        <div className="collapse_quick_filters_button_banner" onClick={() => { setToggleQuickFilters((prev) => !prev) }}>

                            <button
                                title={toggleTableQuickFilters ? "Hide Quick Filters" : "Show Quick Filters"}
                                className={"collapse_quick_filters_button " + (anyFiltersSelected ? "highlight_quick_filters_icon" : "") }
                            >

                                {toggleTableQuickFilters ? "❮" : "🝖"}
                            
                            </button> 

                        </div>
                        }
         
                        {toggleTableQuickFilters &&

                            <div className="filter_groups">

                                <button
                                    onClick={() => { setToggleQuickFilters((prev) => !prev) }}
                                    title={"Hide Quick Filters"}
                                    className={"collapse_quick_filters_button collapse_quick_filters_button--hide " + (anyFiltersSelected ? "highlight_quick_filters_icon" : "") }
                                >

                                    ❮
                                
                                </button> 

                                {/* 
                                <CreateCheckboxFilters
                                    uniqueFilterOptions={uniqueFilterOptions}
                                    selectedFilters={selectedFilters}
                                    filterLabels={filterLabels}
                                    clearFilterGroup={clearFilterGroup}
                                    toggleFilterValue={toggleFilterValue}
                                    setSelectedRows={setSelectedOrderRows}
                                />
                                */}
                                
                                <CreateToggleFilters

                                    // This contains all unique filter options grouped by key
                                    uniqueFilterOptions={uniqueFilterOptions}

                                    // This contains the currently included values for each filter group
                                    selectedFilters={selectedFilters}

                                    // This contains the default included values for each filter group
                                    defaultFilters={defaultFilters}

                                    // This contains the visible labels for each filter group
                                    filterLabels={filterLabels}

                                    // This toggles a single value on or off inside a filter group
                                    toggleFilterValue={toggleFilterValue}

                                    // This sets all values in a filter group to included
                                    setFilterGroupAllOn={setFilterGroupAllOn}

                                    // This sets all values in a filter group to excluded
                                    setFilterGroupAllOff={setFilterGroupAllOff}

                                    // This resets a filter group back to its default included values
                                    resetFilterGroup={resetFilterGroup}

                                    // So we can clear any selected table rows when changing filters
                                    setSelectedRows={setSelectedOrderRows}

                                />
                               
                            </div>

                        }

                    </div>

                    }


                    <div className="orders-table-column">

                        <CustomisableTable
                            tableData={ordersData}
                            initialTableColumns={initialTableColumns}
                            handleSelectRow={handleSelectOrder}
                            filterKeys={filterKeys}
                            filters={selectedFilters}
                            selectedRows={selectedOrderRows}
                            setSelectedRows={setSelectedOrderRows}
                            loadingData={loadingOrders}
                            noDataAvaiableMessage="There are no orders to display between the selected dates or matching your selected filters."
                            numberOfDisplayedDataRows={numberOfDisplayedOrders}
                            setNumberOfDisplayedDataRows={setNumberOfDisplayedOrders}
                            handleRowSingleClick={handleSelectOrder}
                            menuOptions={orderActions}
                            showTax={showTax}
                        />

                    </div>

                </div>

            </div>
        
            }

            
                                  
        </div >

    );
};

export default OrdersTable;
